import { useState } from 'react'
import styled from 'styled-components'
import parseUserAgent from '../../utils/parseUserAgent'

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f5f5f5;
  padding: 1rem;
`

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 2.5rem 2rem;
  max-width: 500px;
  width: 100%;
  text-align: center;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.08);
`

const IconCircle = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background-color: #ffe4e4;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
`

const Badge = styled.span`
  display: inline-block;
  background-color: #ffe4e4;
  color: #d32f2f;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  margin-bottom: 1rem;
  text-transform: uppercase;
`

const Title = styled.h1`
  font-size: 1.4rem;
  font-weight: 700;
  color: #111;
  margin-bottom: 0.75rem;
`

const Message = styled.p`
  font-size: 0.9rem;
  color: #555;
  line-height: 1.6;
  margin-bottom: 1.75rem;
`

const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  width: 100%;
  padding: 0.75rem;
  background-color: #d32f2f;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 0.75rem;
  &:hover {
    background-color: #b71c1c;
  }
`

const SecondaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  width: 100%;
  padding: 0.75rem;
  background-color: #fff;
  color: #333;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 1.25rem;
  &:hover {
    background-color: #f5f5f5;
  }
`

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #eee;
  margin-bottom: 1.25rem;
`

const ReportRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
`

const ReportLink = styled.a`
  color: #888;
  font-size: 0.85rem;
  cursor: pointer;
  text-decoration: none;
  &:hover {
    color: #333;
  }
`

const ReportSeparator = styled.span`
  color: #ddd;
  font-size: 0.85rem;
`

const CopyButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: #888;
  font-size: 0.85rem;
  cursor: pointer;
  &:hover {
    color: #333;
  }
`

const HintText = styled.p`
  font-size: 0.8rem;
  color: #bbb;
  margin: 0 0 0.5rem;
`

const FullPageErrorFallback = () => {
  const [copied, setCopied] = useState(false)

  const isGerman = navigator.language.startsWith('de') || window.location.pathname.includes('/de/')
  const lang = isGerman ? {
    subject: 'Fehlerbericht – Anwendungsfehler',
    to: 'An: support@rigilog.com',
    page: 'Seite',
    time: 'Zeit',
    prompt: 'Bitte beschreiben Sie, was Sie getan haben, als der Fehler aufgetreten ist:',
    hint: 'Problem melden für eine schnellere Lösung:',
    openEmail: 'Per E-Mail melden',
    copyReport: 'Bericht kopieren',
    copied: 'Kopiert!',
  } : {
    subject: 'Bug Report – Application Error',
    to: 'To: support@rigilog.com',
    page: 'Page',
    time: 'Time',
    prompt: 'Please describe what you were doing when the error occurred:',
    hint: 'Report this issue for a faster resolution:',
    openEmail: 'Report via email',
    copyReport: 'Copy report info',
    copied: 'Copied!',
  }

  const reportText = `${lang.to}\nSubject: ${lang.subject}\n\n${lang.page}: ${window.location.href}\n${lang.time}: ${new Date().toLocaleString()}\nBrowser: ${parseUserAgent()}\n\n${lang.prompt}\n`
  const mailtoUrl = `mailto:support@rigilog.com?subject=${encodeURIComponent(lang.subject)}&body=${encodeURIComponent(reportText)}`

  const handleCopy = () => {
    navigator.clipboard.writeText(reportText).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <Wrapper>
      <Card>
        <IconCircle>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d32f2f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </IconCircle>

        <Badge>Application Error</Badge>
        <Title>Something went wrong</Title>
        <Message>
          An unexpected error occurred and the application couldn't recover automatically.
          Your data has not been affected. Please try refreshing — if the problem continues,
          go back to the home screen.
        </Message>

        <PrimaryButton onClick={() => window.location.reload()}>
          ↺ Refresh page
        </PrimaryButton>
        <SecondaryButton onClick={() => { window.location.href = '/' }}>
          ⌂ Go to Home
        </SecondaryButton>

        <Divider />
        <HintText>{lang.hint}</HintText>
        <ReportRow>
          <ReportLink href={mailtoUrl}>⚑ {lang.openEmail}</ReportLink>
          <ReportSeparator>|</ReportSeparator>
          <CopyButton onClick={handleCopy}>
            {copied ? `✓ ${lang.copied}` : `📋 ${lang.copyReport}`}
          </CopyButton>
        </ReportRow>
      </Card>
    </Wrapper>
  )
}

export default FullPageErrorFallback
